import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/customRepeatEveryStyle';
import { BodyS } from 'components/Typography/Typography';
import Info from '@material-ui/icons/InfoOutlined';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { translate } from 'components/Translation';
import { PeriodUnit } from '../CustomRepeatEvery';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICustomRepeatEverySummaryProps {
	numberOfOrders: number;
	periodUnit: PeriodUnit;
	periodicity: string;
	startDate: string;
	endDate: string;
}

const CustomRepeatEverySummary = ({
	numberOfOrders,
	periodUnit,
	periodicity,
	startDate,
	endDate,
}: ICustomRepeatEverySummaryProps) => {
	const classes = useStyles();
	const periodNumber = useMemo(() => {
		switch (periodUnit) {
			case PeriodUnit.DAY:
				return Number(periodicity) === 1
					? translate('custom.repeat.every.day')
					: translate('custom.repeat.every.days');
			case PeriodUnit.WEEK:
				return Number(periodicity) === 1
					? translate('custom.repeat.every.week')
					: translate('custom.repeat.every.weeks');
			case PeriodUnit.MONTH:
				return Number(periodicity) === 1
					? translate('custom.repeat.every.month')
					: translate('custom.repeat.every.months');
			case PeriodUnit.YEAR:
				return Number(periodicity) === 1
					? translate('custom.repeat.every.year')
					: translate('custom.repeat.every.years');
		}
	}, [periodUnit, periodicity]);

	return (
		<div className={classes.customRepeatSummaryWrapper}>
			<div className={classes.summaryInfo}>
				<Info />
			</div>
			<div>
				<div className={classes.customRepeatSummaryLine1Wrapper}>
					<BodyS disableMargin color="secondary-light">
						{numberOfOrders === 1
							? translate('custom.repeat.every.summary.order')
							: translate('custom.repeat.every.summary.orders', numberOfOrders)}
					</BodyS>
					<ConditionalRender
						show={numberOfOrders > 1}
						onTrue={() => (
							<BodyS disableMargin color="secondary-light">
								{translate('custom.repeat.every.summary.period', periodicity, periodNumber)}
							</BodyS>
						)}
					/>
				</div>
				<div className={classes.customRepeatSummaryLine2Wrapper}>
					<ConditionalRender
						show={numberOfOrders > 1}
						onTrue={() => (
							<>
								<div className={classes.customRepeatSummaryFrom}>
									<BodyS disableMargin color="secondary-light">
										{translate('custom.repeat.every.summary.from')}
									</BodyS>
								</div>
								<div className={classes.customRepeatSummaryStartDate}>
									<DateRangeIcon className={classes.icon} />
									<BodyS disableMargin>{startDate}</BodyS>
								</div>
								<div className={classes.customRepeatSummaryToDate}>
									<div className={classes.customRepeatSummaryTo}>
										<BodyS disableMargin color="secondary-light">
											{translate('custom.repeat.every.summary.to')}
										</BodyS>
									</div>
									<div className={classes.customRepeatSummaryEndDate}>
										<DateRangeIcon className={classes.icon} />
										<BodyS disableMargin>{endDate}</BodyS>
									</div>
								</div>
							</>
						)}
						onFalse={() => (
							<>
								<div className={classes.customRepeatSummaryFrom}>
									<BodyS disableMargin color="secondary-light">
										{translate('custom.repeat.every.summary.on')}
									</BodyS>
								</div>
								<div className={classes.customRepeatSummaryStartDate}>
									<DateRangeIcon className={classes.icon} />
									<BodyS disableMargin>{startDate}</BodyS>
								</div>
							</>
						)}
					/>
				</div>
			</div>
		</div>
	);
};

export default CustomRepeatEverySummary;
